import { CareRecord, type CareState } from './CareRecord';

/**
 * The household: a care code shared between phones, an optional display name
 * for the leaderboard, and per-dog state synced to api.lunaloo.net.
 *
 * Offline-first: everything lives in localStorage and works without the
 * server; pushes are debounced and retried when the network comes back.
 */
export interface FoodState {
  bags: number;
  torch?: boolean;
  treats?: number;
  /** the last day a holiday put extra treats in the jar (YYYY-MM-DD) */
  treatDay?: string;
  fill: number;
  hunger: number;
  at: number;
}
export interface DogDoc {
  care: CareState;
  food?: FoodState;
  tricks?: Record<string, number>;
  needs?: Record<string, unknown>;
  business?: Record<string, unknown>;
}
export interface SharedPhoto {
  at: number;
  device: string;
  caption: string;
  by: string;
  avatar: string;
  url: string;
}
export interface HistoryEntry {
  id: number;
  at: number;
  note: string | null;
  days: number;
  streakBest: number;
  lastDay: string | null;
  bowl: number;
  bags: number;
  hunger: number;
}
export interface LeaderboardEntry {
  name: string;
  dogs: string[];
  bestStreak: number;
  days: number;
  fetches: number;
  earned: number;
  holes: number;
  cans: number;
  gameBest: number;
  bestFetch: number;
  squirrelWins: number;
  routines: number;
  catches: number;
  agilityBest: number;
  agilityRuns: number;
  lastDay: string | null;
}

const API = 'https://api.lunaloo.net';
const CODE_KEY = 'lunaloo.code';
const NAME_KEY = 'lunaloo.name';

export class Household {
  code: string | null = null;
  name = '';
  readonly care: CareRecord;
  onStatus?: (text: string) => void;
  private pushTimer = 0;
  private dirty = false;
  private foodProvider: (() => FoodState) | null = null;
  private foodApplier: ((f: FoodState) => void) | null = null;
  private tricksProvider: (() => Record<string, number>) | null = null;
  private tricksApplier: ((t: Record<string, number>) => void) | null = null;
  private needsProvider: (() => Record<string, unknown>) | null = null;
  private needsApplier: ((n: Record<string, unknown>) => void) | null = null;
  private businessProvider: (() => Record<string, unknown>) | null = null;
  private businessApplier: ((n: Record<string, unknown>) => void) | null = null;

  constructor(readonly dog: string) {
    this.code = localStorage.getItem(CODE_KEY);
    this.name = localStorage.getItem(NAME_KEY) ?? '';
    const raw = localStorage.getItem(`lunaloo.care.${dog}`);
    this.care = new CareRecord(raw ? (JSON.parse(raw) as CareState) : undefined);
    this.care.onChange = () => this.markDirty();
    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') void this.push();
      else void this.pull();
    });
    window.addEventListener('online', () => void this.push());
  }

  /** the feeding loop registers how to read/apply its state so it syncs too */
  bindFood(get: () => FoodState, apply: (f: FoodState) => void): void {
    this.foodProvider = get;
    this.foodApplier = apply;
  }

  bindTricks(get: () => Record<string, number>, apply: (t: Record<string, number>) => void): void {
    this.tricksProvider = get;
    this.tricksApplier = apply;
  }

  bindNeeds(get: () => Record<string, unknown>, apply: (n: Record<string, unknown>) => void): void {
    this.needsProvider = get;
    this.needsApplier = apply;
  }

  bindBusiness(get: () => Record<string, unknown>, apply: (n: Record<string, unknown>) => void): void {
    this.businessProvider = get;
    this.businessApplier = apply;
  }

  markDirty(): void {
    localStorage.setItem(`lunaloo.care.${this.dog}`, JSON.stringify(this.care.state));
    this.dirty = true;
    window.clearTimeout(this.pushTimer);
    this.pushTimer = window.setTimeout(() => void this.push(), 3000);
  }

  /** photos taken in the app, shared with everyone on the license (newest first) */
  async photos(): Promise<SharedPhoto[]> {
    if (!this.code) return [];
    try {
      const res = await this.api(`/v1/households/${this.code}/dogs/${this.dog}/photos`);
      const j = (await res.json()) as { photos: SharedPhoto[] };
      return j.photos.map((p) => ({ ...p, url: `${API}${p.url}` }));
    } catch {
      return [];
    }
  }

  async sharePhoto(device: string, snap: { at: number; caption: string; dataUrl: string }): Promise<boolean> {
    if (!this.code) return false;
    try {
      await this.api(`/v1/households/${this.code}/dogs/${this.dog}/photos`, {
        method: 'POST',
        body: JSON.stringify({ device, at: snap.at, caption: snap.caption, dataUrl: snap.dataUrl }),
      });
      return true;
    } catch {
      return false;
    }
  }

  /** the one who took it, or the owner, can delete a shared photo */
  async deletePhoto(device: string, at: number): Promise<boolean> {
    if (!this.code) return false;
    try {
      await this.api(`/v1/households/${this.code}/dogs/${this.dog}/photos/${at}?by=${encodeURIComponent(device)}`, { method: 'DELETE' });
      return true;
    } catch {
      return false;
    }
  }

  private async api(path: string, init?: RequestInit): Promise<Response> {
    const res = await fetch(`${API}${path}`, { ...init, headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) } });
    if (res.status === 404 && this.code && path.includes(`/households/${this.code}/`)) {
      // the server no longer knows this code: forget it and mint a new one next time (local state is kept).
      // Only on the server's own word — a 404 for a route this API version doesn't have must not cost the code.
      let detail = '';
      try {
        detail = String(((await res.clone().json()) as { detail?: string }).detail ?? '');
      } catch {
        /* not json */
      }
      if (detail === 'unknown care code') {
        this.code = null;
        localStorage.removeItem(CODE_KEY);
      }
    }
    if (!res.ok) throw new Error(`${res.status} ${path}`);
    return res;
  }

  /** first run: mint a code (needs the network once) */
  async ensureCode(): Promise<string | null> {
    if (this.code) return this.code;
    try {
      const { code } = (await (await this.api('/v1/codes', { method: 'POST' })).json()) as { code: string };
      this.code = code;
      localStorage.setItem(CODE_KEY, code);
      return code;
    } catch {
      return null;
    }
  }

  /** link this phone to an existing household's code; pulls its state */
  async link(code: string): Promise<boolean> {
    code = code.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (code.length !== 10) return false;
    code = `${code.slice(0, 5)}-${code.slice(5)}`;
    try {
      const h = (await (await this.api(`/v1/households/${code}`)).json()) as { name: string | null };
      this.code = code;
      localStorage.setItem(CODE_KEY, code);
      if (h.name) {
        this.name = h.name;
        localStorage.setItem(NAME_KEY, h.name);
      }
      await this.pull();
      await this.push();
      return true;
    } catch {
      return false;
    }
  }

  async setName(name: string): Promise<void> {
    this.name = name.trim().slice(0, 40);
    localStorage.setItem(NAME_KEY, this.name);
    const code = await this.ensureCode();
    if (!code) return;
    try {
      await this.api(`/v1/households/${code}/name`, { method: 'PUT', body: JSON.stringify({ name: this.name }) });
    } catch {
      /* retry next time */
    }
  }

  async pull(): Promise<void> {
    if (!this.code) return;
    try {
      const { state } = (await (await this.api(`/v1/households/${this.code}/dogs/${this.dog}`)).json()) as { state: DogDoc | null };
      if (!state) return;
      if (state.care) this.care.merge(state.care);
      if (state.food && this.foodApplier) this.foodApplier(state.food);
      if (state.tricks && this.tricksApplier) this.tricksApplier(state.tricks);
      if (state.needs && this.needsApplier) this.needsApplier(state.needs);
      if (state.business && this.businessApplier) this.businessApplier(state.business);
      this.onStatus?.('synced');
    } catch {
      this.onStatus?.('offline');
    }
  }

  async push(): Promise<void> {
    const code = await this.ensureCode();
    if (!code || !navigator.onLine) return;
    const doc: DogDoc = { care: this.care.state, food: this.foodProvider?.(), tricks: this.tricksProvider?.(), needs: this.needsProvider?.(), business: this.businessProvider?.() };
    try {
      const { state } = (await (
        await this.api(`/v1/households/${code}/dogs/${this.dog}`, { method: 'PUT', body: JSON.stringify({ state: doc }) })
      ).json()) as { state: DogDoc };
      // the server merged; take its view of the care record and trick levels
      if (state.care) this.care.state = state.care;
      if (state.tricks && this.tricksApplier) this.tricksApplier(state.tricks);
      localStorage.setItem(`lunaloo.care.${this.dog}`, JSON.stringify(this.care.state));
      this.dirty = false;
      this.onStatus?.('synced');
    } catch {
      this.onStatus?.('offline');
    }
  }

  get hasUnsynced(): boolean {
    return this.dirty;
  }

  /** the time machine: recent saved versions of this dog's state */
  async history(): Promise<HistoryEntry[]> {
    if (!this.code) return [];
    try {
      const { versions } = (await (await this.api(`/v1/households/${this.code}/dogs/${this.dog}/history?limit=30`)).json()) as { versions: HistoryEntry[] };
      return versions;
    } catch {
      return [];
    }
  }

  /** make a saved version current (reversible: the current state is saved first) */
  async restore(id: number): Promise<boolean> {
    if (!this.code) return false;
    try {
      await this.api(`/v1/households/${this.code}/dogs/${this.dog}/restore`, { method: 'POST', body: JSON.stringify({ id }) });
      // drop local copies so the restored state wins on reload
      localStorage.removeItem(`lunaloo.care.${this.dog}`);
      localStorage.removeItem(`lunaloo.food.${this.dog}`);
      localStorage.removeItem(`lunaloo.needs.${this.dog}`);
      localStorage.removeItem(`lunaloo.tricks.${this.dog}`);
      return true;
    } catch {
      return false;
    }
  }

  async leaderboard(): Promise<LeaderboardEntry[]> {
    try {
      const { entries } = (await (await this.api('/v1/leaderboard?dog=all&limit=100')).json()) as { entries: LeaderboardEntry[] };
      return entries;
    } catch {
      return [];
    }
  }
}
